import { RefreshCw, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError, apiRequest } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { WorkspaceLayout } from '../../layouts/WorkspaceLayout';
import { ErrorState, Spinner } from '../../shared/components';

type ExchangeRate = {
  thb_per_point: number;
  minimum_redeem_thb?: number;
  effective_at?: string;
  version?: number;
  updated_by?: string;
};

type ExchangeRateHistory = {
  audit_id: string;
  admin_id?: string;
  admin_name?: string;
  before_rate?: number;
  after_rate?: number;
  note?: string;
  created_at?: string;
};
type ExchangeRateResponse = { rate: ExchangeRate; history: ExchangeRateHistory[] };

function rate(value: unknown) {
  return Number(value || 0).toLocaleString('th-TH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
}

function dateTime(value?: string) {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' });
}

export function PointExchangeRatePage() {
  const { session } = useAuth();
  const adminId = session?.expert_id || '';
  const [data, setData] = useState<ExchangeRateResponse | null>(null);
  const [value, setValue] = useState('1');
  const [minimumRedeem, setMinimumRedeem] = useState('50');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = async () => {
    if (!adminId) return;
    setLoading(true);
    setError('');
    try {
      const next = await apiRequest<ExchangeRateResponse>('/admin/economy/point-exchange-rate', {
        query: { admin_id: adminId, history_limit: 20 },
      });
      setData(next);
      setValue(String(next.rate.thb_per_point));
      setMinimumRedeem(String(next.rate.minimum_redeem_thb ?? 50));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'โหลดอัตราแลกคะแนนไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [adminId]);

  const save = async () => {
    const thbPerPoint = Number(value);
    const minimumRedeemThb = Number(minimumRedeem);
    if (!Number.isFinite(thbPerPoint) || thbPerPoint <= 0) {
      setError('อัตราแลกเปลี่ยนต้องมากกว่า 0 บาทต่อ 1 Point');
      return;
    }
    if (!Number.isFinite(minimumRedeemThb) || minimumRedeemThb < 1) {
      setError('ขั้นต่ำถอนต้องไม่น้อยกว่า 1 บาท');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const next = await apiRequest<ExchangeRateResponse>('/admin/economy/point-exchange-rate', {
        method: 'PUT',
        body: {
          admin_id: adminId,
          thb_per_point: thbPerPoint,
          minimum_redeem_thb: minimumRedeemThb,
          note: note.trim() || 'ปรับอัตราแลก Point เป็นบาทโดย Admin',
        },
      });
      setData(next);
      setValue(String(next.rate.thb_per_point));
      setMinimumRedeem(String(next.rate.minimum_redeem_thb ?? 50));
      setNote('');
      setMessage('บันทึกแล้ว — คำขอถอนที่สร้างใหม่จะใช้อัตรานี้');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'บันทึกอัตราแลกคะแนนไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  return <WorkspaceLayout kind="admin" title="อัตราแลก Point / บาท" description="กำหนดมูลค่า Point สำหรับ Creator Wallet และคำขอถอนใหม่">
    {loading ? <section className="panel"><Spinner label="กำลังโหลดอัตราแลกคะแนน" /></section> : error && !data ? <section className="panel"><ErrorState message={error} retry={load} /></section> : <>
      <section className="panel">
        <p className="eyebrow">POINT EXCHANGE RATE</p>
        <h2>1 Point = {rate(data?.rate.thb_per_point)} บาท</h2>
        <p>มีผลตั้งแต่ {dateTime(data?.rate.effective_at)} · Version {data?.rate.version || 1}</p>
      </section>
      <section className="panel">
        <div className="panel-title"><div><h2>ปรับอัตราแลกคะแนน</h2><p>ใช้กับ Creator Wallet และล็อกเป็น snapshot เมื่อส่งคำขอถอน เพื่อไม่กระทบรายการเดิม</p></div><button className="button button--ghost" onClick={() => void load()} disabled={loading || saving}><RefreshCw size={16} />รีเฟรช</button></div>
        <div className="wallet-ledger-filters">
          <label>1 Point เท่ากับ (บาท)<input aria-label="1 Point เท่ากับบาท" type="number" min="0.0001" max="10000" step="0.01" value={value} onChange={(event) => setValue(event.target.value)} /></label>
          <label>ถอนขั้นต่ำ (บาท)<input aria-label="ถอนขั้นต่ำบาท" type="number" min="1" step="1" value={minimumRedeem} onChange={(event) => setMinimumRedeem(event.target.value)} /></label>
          <label>หมายเหตุสำหรับ Audit log<input value={note} maxLength={500} placeholder="เช่น ปรับตามนโยบายเดือนนี้" onChange={(event) => setNote(event.target.value)} /></label>
        </div>
        {error && <p className="form-error">{error}</p>}
        {message && <p className="form-success">{message}</p>}
        <button className="button button--primary" onClick={() => void save()} disabled={saving || !adminId}><Save size={16} />{saving ? 'กำลังบันทึก…' : 'บันทึกอัตรา'}</button>
      </section>
      <section className="panel wallet-table-shell">
        <div className="panel-title"><h2>ประวัติการปรับ</h2></div>
        {(data?.history || []).length ? <div className="table-scroll"><table><thead><tr><th>อัตรา</th><th>มีผลตั้งแต่</th><th>ผู้ปรับ</th><th>หมายเหตุ</th></tr></thead><tbody>{data?.history.map((item, index) => <tr key={item.audit_id || String(index)}><td>1 Point = {rate(item.after_rate)} บาท</td><td>{dateTime(item.created_at)}</td><td>{item.admin_name || item.admin_id || 'System'}</td><td>{item.note || '—'}</td></tr>)}</tbody></table></div> : <p>ยังไม่มีประวัติการปรับ</p>}
      </section>
    </>}
  </WorkspaceLayout>;
}
